'use client';

import { useState, useEffect } from 'react';
import { Server, Wifi, WifiOff, Users, Loader2, Signal, Gamepad2 } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import Image from '@/components/ui/Image';

interface ServerStatusData {
  online: boolean;
  motd?: { clean: string[] };
  players?: {
    online: number;
    max: number;
    list?: { name: string; uuid: string }[];
  };
  version?: string;
  icon?: string;
}

export function ServerStatus({ ip }: { ip: string }) {
  const [status, setStatus] = useState<ServerStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const response = await fetch(`https://api.mcsrvstat.us/3/${ip}`);
        if (!response.ok) throw new Error('status-api');
        const data = await response.json();
        if (!cancelled) { setStatus(data); setError(null); }
      } catch (err) {
        if (!cancelled) setError('No se pudo obtener el estado del servidor.');
        console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [ip]);

  // ── Cargando ──
  if (loading) {
    return (
      <div className="relative flex h-44 items-center justify-center gap-3 overflow-hidden rounded-3xl border border-emerald-500/20 bg-card/40">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
        <p className="text-muted-foreground">Conectando con el servidor…</p>
      </div>
    );
  }

  // ── Offline ──
  if (error || !status || !status.online) {
    return (
      <div className="relative overflow-hidden rounded-3xl border border-rose-500/25 bg-gradient-to-br from-rose-500/10 to-card/30 p-6">
        <div className="flex items-center gap-4">
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-rose-500/30 bg-rose-500/10 text-rose-400">
            <WifiOff className="h-7 w-7" />
          </div>
          <div>
            <h3 className="font-headline text-xl font-bold text-foreground">Servidor offline</h3>
            <p className="text-sm text-muted-foreground">
              El servidor no responde ahora mismo. Vuelve a intentarlo en unos minutos.
            </p>
          </div>
          <span className="ml-auto hidden items-center gap-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-xs font-semibold text-rose-300 sm:flex">
            <span className="h-2 w-2 rounded-full bg-rose-500" /> Desconectado
          </span>
        </div>
      </div>
    );
  }

  const online = status.players?.online ?? 0;
  const max = status.players?.max ?? 0;
  const list = status.players?.list ?? [];
  const pct = max > 0 ? Math.min(100, Math.round((online / max) * 100)) : 0;
  const title = status.motd?.clean?.[0]?.trim() || 'Servidor de Jartiland';
  const subtitle = status.motd?.clean?.slice(1).join(' ').trim();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative overflow-hidden rounded-3xl border border-emerald-500/25 bg-card/40 shadow-xl shadow-emerald-500/5"
    >
      {/* Fondo: icono del servidor difuminado + glow + rejilla */}
      {status.icon && (
        <Image src={status.icon} alt="" className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-[0.07] blur-2xl" />
      )}
      <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 22px, currentColor 22px, currentColor 23px), repeating-linear-gradient(90deg, transparent, transparent 22px, currentColor 22px, currentColor 23px)',
        }}
      />

      <div className="relative p-5 sm:p-6">
        {/* Cabecera */}
        <div className="flex items-start gap-4">
          {status.icon ? (
            <Image src={status.icon} alt="Icono del servidor" width={64} height={64}
              className="h-16 w-16 rounded-2xl border-2 border-emerald-500/30 shadow-lg shadow-emerald-500/10" />
          ) : (
            <div className="grid h-16 w-16 place-items-center rounded-2xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
              <Server className="h-8 w-8" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <h3 className="truncate font-headline text-xl font-bold text-foreground sm:text-2xl">{title}</h3>
            {subtitle && <p className="truncate text-sm text-muted-foreground">{subtitle}</p>}
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {status.version && (
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-muted/40 px-2 py-0.5 text-[0.65rem] font-medium text-muted-foreground">
                  <Gamepad2 className="h-3 w-3" /> {status.version}
                </span>
              )}
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[0.65rem] font-medium text-emerald-300">
                <Signal className="h-3 w-3" /> Latido cada 30s
              </span>
            </div>
          </div>

          {/* Estado online con pulso */}
          <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-300">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
            </span>
            Online
          </div>
        </div>

        {/* Aforo */}
        <div className="mt-5">
          <div className="mb-1.5 flex items-end justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-emerald-400" />
              <span className="text-2xl font-bold text-foreground">{online}</span>
              <span className="text-sm text-muted-foreground">/ {max} jugadores conectados</span>
            </div>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted/40">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-300"
            />
          </div>
        </div>

        {/* Jugadores con nombre */}
        <div className="mt-4">
          {list.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              <AnimatePresence>
                {list.map((player) => (
                  <motion.div
                    key={player.uuid || player.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex items-center gap-2 rounded-full border border-white/10 bg-card/60 py-1 pl-1 pr-3 transition-colors hover:border-emerald-500/40 hover:bg-emerald-500/5"
                  >
                    <img
                      src={`https://mc-heads.net/avatar/${player.uuid || player.name}/32`}
                      alt={player.name}
                      width={24}
                      height={24}
                      className="h-6 w-6 rounded-full"
                      onError={(e) => { (e.currentTarget.style.display = 'none'); }}
                    />
                    <span className="text-sm font-medium text-foreground">{player.name}</span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : online > 0 ? (
            <p className="rounded-xl border border-white/8 bg-muted/20 px-4 py-3 text-center text-sm text-muted-foreground">
              Hay {online} {online === 1 ? 'jugador conectado' : 'jugadores conectados'}, pero el servidor no comparte sus nombres.
            </p>
          ) : (
            <p className="rounded-xl border border-white/8 bg-muted/20 px-4 py-3 text-center text-sm text-muted-foreground">
              No hay nadie conectado ahora mismo. ¡Sé el primero! ⚔️
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
